/**
 * Inactivity Timeout Utility
 * Tracks user activity and redirects to a return URL after specified timeout period
 */

class InactivityTimeout {
  constructor() {
    this.timeout = null;
    this.returnUrl = null;
    this.timeoutDuration = 0;
    this.warningDuration = 10; // seconds before timeout to show warning
    this.timer = null;
    this.warningTimer = null;
    this.isActive = false;
    this.warningShown = false;
    
    // Bind methods to preserve context
    this.resetTimer = this.resetTimer.bind(this);
    this.handleTimeout = this.handleTimeout.bind(this);
    this.showWarning = this.showWarning.bind(this);
    this.hideWarning = this.hideWarning.bind(this);
  }

  /**
   * Initialize the inactivity timeout from URL parameters
   */
  init() {
    console.log('🚨 INACTIVITY TIMEOUT: Initializing...');
    console.log('🚨 Current URL:', window.location.href);
    console.log('🚨 Search params:', window.location.search);
    
    const urlParams = new URLSearchParams(window.location.search);
    const timeoutParam = urlParams.get('timeout');
    const returnUrlParam = urlParams.get('returnUrl');
    
    console.log('🚨 Timeout param:', timeoutParam);
    console.log('🚨 Return URL param:', returnUrlParam);

    // Only activate if both parameters are present
    if (timeoutParam && returnUrlParam) {
      this.timeout = parseInt(timeoutParam, 10);
      this.returnUrl = returnUrlParam;
      this.timeoutDuration = this.timeout * 1000; // Convert to milliseconds

      console.log('🚨 Parsed timeout:', this.timeout);
      console.log('🚨 Parsed return URL:', this.returnUrl);

      if (this.timeout > 0 && this.returnUrl) {
        this.start();
        console.log(`🚨 INACTIVITY TIMEOUT STARTED: ${this.timeout}s, return to: ${this.returnUrl}`);
      } else {
        console.log('🚨 Invalid timeout or return URL');
      }
    } else {
      console.log('🚨 Missing timeout or returnUrl parameters - timeout not activated');
    }
  }

  /**
   * Start the inactivity timeout
   */
  start() {
    if (this.isActive) return;
    
    this.isActive = true;
    this.addEventListeners();
    this.resetTimer();
  }

  /**
   * Stop the inactivity timeout
   */
  stop() {
    if (!this.isActive) return;
    
    this.isActive = false;
    this.removeEventListeners();
    this.clearTimer();
    this.hideWarning();
  }

  /**
   * Add event listeners for user activity
   */
  addEventListeners() {
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'touchmove',
      'click',
      'keydown'
    ];

    events.forEach(event => {
      document.addEventListener(event, this.resetTimer, true);
    });
  }

  /**
   * Remove event listeners
   */
  removeEventListeners() {
    const events = [
      'mousedown',
      'mousemove',
      'keypress', 
      'scroll',
      'touchstart',
      'touchmove',
      'click',
      'keydown'
    ];

    events.forEach(event => {
      document.removeEventListener(event, this.resetTimer, true);
    });
  }

  /**
   * Reset the inactivity timer
   */
  resetTimer() {
    if (!this.isActive) return;

    console.log('🚨 Timer reset due to user activity');
    this.clearTimer();
    this.hideWarning();

    // Set warning timer (10 seconds before timeout)
    if (this.timeoutDuration > this.warningDuration * 1000) {
      this.warningTimer = setTimeout(
        this.showWarning, 
        this.timeoutDuration - (this.warningDuration * 1000)
      );
    }

    // Set main timeout timer
    this.timer = setTimeout(this.handleTimeout, this.timeoutDuration);
  }

  /**
   * Clear all timers
   */
  clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * Show timeout warning
   */
  showWarning() {
    if (this.warningShown || !this.isActive) return;
    
    this.warningShown = true;
    
    // Create warning overlay
    const overlay = document.createElement('div');
    overlay.id = 'inactivity-warning-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.8);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-family: Arial, sans-serif;
    `;

    const warningBox = document.createElement('div');
    warningBox.style.cssText = `
      background-color: #f44336;
      padding: 30px;
      border-radius: 10px;
      text-align: center;
      max-width: 400px;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    `;

    warningBox.innerHTML = `
      <h3 style="margin: 0 0 15px 0; font-size: 24px;">Session Timeout Warning</h3>
      <p style="margin: 0 0 20px 0; font-size: 18px;">
        You will be returned to the main screen in <span id="countdown">${this.warningDuration}</span> seconds due to inactivity.
      </p>
      <p style="margin: 0; font-size: 16px; opacity: 0.9;">
        Touch the screen or move the mouse to continue.
      </p>
    `;

    overlay.appendChild(warningBox);
    document.body.appendChild(overlay);

    // Start countdown
    let countdown = this.warningDuration;
    const countdownElement = document.getElementById('countdown');
    const countdownInterval = setInterval(() => {
      countdown--;
      if (countdownElement) {
        countdownElement.textContent = countdown;
      }
      if (countdown <= 0) {
        clearInterval(countdownInterval);
      }
    }, 1000);

    console.log(`Showing inactivity warning: ${this.warningDuration}s remaining`);
  }

  /**
   * Hide timeout warning
   */
  hideWarning() {
    const overlay = document.getElementById('inactivity-warning-overlay');
    if (overlay) {
      overlay.remove();
      this.warningShown = false;
      console.log('Inactivity warning hidden');
    }
  }

  /**
   * Handle timeout - redirect to return URL
   */
  handleTimeout() {
    if (!this.isActive || !this.returnUrl) return;
    
    console.log(`Inactivity timeout reached. Redirecting to: ${this.returnUrl}`);
    
    // Cleanup before redirect
    this.stop();
    
    // Redirect to return URL
    window.location.href = this.returnUrl;
  }
}

// Create singleton instance
const inactivityTimeout = new InactivityTimeout();

export default inactivityTimeout;